import { markRaw, type Component } from 'vue';
import {
  AlertCircle,
  AlertTriangle,
  Archive,
  Bell,
  Bookmark,
  Briefcase,
  Building2,
  Calendar,
  Camera,
  Check,
  CheckCircle,
  CircleHelp,
  ClipboardList,
  Clock,
  Copy,
  Download,
  Factory,
  File,
  FileText,
  Filter,
  Folder,
  FolderOpen,
  Globe,
  HardHat,
  Heart,
  History,
  Image,
  Info,
  Link,
  Lock,
  Mail,
  MapPin,
  MessageSquare,
  Mic,
  Monitor,
  Pencil,
  Phone,
  PieChart,
  Plus,
  Printer,
  RefreshCw,
  Send,
  Settings,
  Shield,
  Star,
  Target,
  Trash2,
  Truck,
  Upload,
  User,
  Users,
  Video,
  Wrench,
  X,
} from 'lucide-vue-next';
import {
  DIRECTORY_MODULE_DESIGN_ICON_LABELS,
  DIRECTORY_MODULE_DESIGN_ICONS,
} from './directoryModuleDesignIcons';

const LUCIDE_MODULE_ICONS = [
  { id: 'target', icon: markRaw(Target) },
  { id: 'wrench', icon: markRaw(Wrench) },
  { id: 'factory', icon: markRaw(Factory) },
  { id: 'folder', icon: markRaw(Folder) },
  { id: 'file', icon: markRaw(File) },
  { id: 'user', icon: markRaw(User) },
  { id: 'users', icon: markRaw(Users) },
  { id: 'mail', icon: markRaw(Mail) },
  { id: 'message', icon: markRaw(MessageSquare) },
  { id: 'send', icon: markRaw(Send) },
  { id: 'settings', icon: markRaw(Settings) },
  { id: 'filter', icon: markRaw(Filter) },
  { id: 'plus', icon: markRaw(Plus) },
  { id: 'x', icon: markRaw(X) },
  { id: 'check', icon: markRaw(Check) },
  { id: 'check-circle', icon: markRaw(CheckCircle) },
  { id: 'alert-circle', icon: markRaw(AlertCircle) },
  { id: 'alert-triangle', icon: markRaw(AlertTriangle) },
  { id: 'info', icon: markRaw(Info) },
  { id: 'help', icon: markRaw(CircleHelp) },
  { id: 'calendar', icon: markRaw(Calendar) },
  { id: 'clock', icon: markRaw(Clock) },
  { id: 'history', icon: markRaw(History) },
  { id: 'file-text', icon: markRaw(FileText) },
  { id: 'clipboard', icon: markRaw(ClipboardList) },
  { id: 'archive', icon: markRaw(Archive) },
  { id: 'folder-open', icon: markRaw(FolderOpen) },
  { id: 'briefcase', icon: markRaw(Briefcase) },
  { id: 'building', icon: markRaw(Building2) },
  { id: 'hard-hat', icon: markRaw(HardHat) },
  { id: 'shield', icon: markRaw(Shield) },
  { id: 'lock', icon: markRaw(Lock) },
  { id: 'link', icon: markRaw(Link) },
  { id: 'globe', icon: markRaw(Globe) },
  { id: 'map-pin', icon: markRaw(MapPin) },
  { id: 'heart', icon: markRaw(Heart) },
  { id: 'star', icon: markRaw(Star) },
  { id: 'bookmark', icon: markRaw(Bookmark) },
  { id: 'bell', icon: markRaw(Bell) },
  { id: 'camera', icon: markRaw(Camera) },
  { id: 'image', icon: markRaw(Image) },
  { id: 'video', icon: markRaw(Video) },
  { id: 'mic', icon: markRaw(Mic) },
  { id: 'monitor', icon: markRaw(Monitor) },
  { id: 'printer', icon: markRaw(Printer) },
  { id: 'phone', icon: markRaw(Phone) },
  { id: 'truck', icon: markRaw(Truck) },
  { id: 'pie-chart', icon: markRaw(PieChart) },
  { id: 'pencil', icon: markRaw(Pencil) },
  { id: 'copy', icon: markRaw(Copy) },
  { id: 'download', icon: markRaw(Download) },
  { id: 'upload', icon: markRaw(Upload) },
  { id: 'refresh', icon: markRaw(RefreshCw) },
  { id: 'trash', icon: markRaw(Trash2) },
] as const satisfies ReadonlyArray<{ id: string; icon: Component }>;

export const DIRECTORY_MODULE_ICONS = [
  ...DIRECTORY_MODULE_DESIGN_ICONS,
  ...LUCIDE_MODULE_ICONS,
] as const;

export type DirectoryIconId = (typeof DIRECTORY_MODULE_ICONS)[number]['id'];

export type DirectoryIcon = (typeof DIRECTORY_MODULE_ICONS)[number]['icon'];

export const DIRECTORY_MODULE_ICON_LABELS: Record<DirectoryIconId, string> = {
  ...DIRECTORY_MODULE_DESIGN_ICON_LABELS,
  target: 'Alvo',
  wrench: 'Chave',
  factory: 'Fábrica',
  folder: 'Pasta',
  file: 'Arquivo',
  user: 'Usuário',
  users: 'Usuários',
  mail: 'E-mail',
  message: 'Mensagem',
  send: 'Enviar',
  settings: 'Configurações',
  filter: 'Filtro',
  plus: 'Adicionar',
  x: 'Fechar',
  check: 'Confirmar',
  'check-circle': 'Confirmado',
  'alert-circle': 'Alerta',
  'alert-triangle': 'Aviso',
  info: 'Informação',
  help: 'Ajuda',
  calendar: 'Calendário',
  clock: 'Relógio',
  history: 'Histórico',
  'file-text': 'Documento',
  clipboard: 'Prancheta',
  archive: 'Arquivo morto',
  'folder-open': 'Pasta aberta',
  briefcase: 'Maleta',
  building: 'Edifício',
  'hard-hat': 'Capacete',
  shield: 'Escudo',
  lock: 'Cadeado',
  link: 'Link',
  globe: 'Globo',
  'map-pin': 'Localização',
  heart: 'Coração',
  star: 'Estrela',
  bookmark: 'Marcador',
  bell: 'Sino',
  camera: 'Câmera',
  image: 'Imagem',
  video: 'Vídeo',
  mic: 'Microfone',
  monitor: 'Monitor',
  printer: 'Impressora',
  phone: 'Telefone',
  truck: 'Caminhão',
  'pie-chart': 'Gráfico',
  pencil: 'Lápis',
  copy: 'Copiar',
  download: 'Download',
  upload: 'Upload',
  refresh: 'Atualizar',
  trash: 'Lixeira',
};

const iconById = new Map<string, DirectoryIcon>(
  DIRECTORY_MODULE_ICONS.map((entry) => [entry.id, entry.icon])
);

export function isDirectoryIconId(value: string): value is DirectoryIconId {
  return iconById.has(value);
}

// The `.svg` modules type as `string` under vite/client, but vite-svg-loader delivers components.
export function getDirectoryModuleIcon(id: string | undefined): Component {
  if (id && iconById.has(id)) {
    return iconById.get(id) as Component;
  }
  return iconById.get(DEFAULT_DIRECTORY_ICON_ID) as Component;
}

export function getDirectoryModuleIconLabel(id: string): string {
  if (isDirectoryIconId(id)) {
    return DIRECTORY_MODULE_ICON_LABELS[id];
  }
  return id;
}

export const DEFAULT_DIRECTORY_ICON_ID: DirectoryIconId = 'target';

export function resolveIconId(iconId: string | null | undefined): DirectoryIconId | undefined {
  if (iconId && isDirectoryIconId(iconId)) {
    return iconId;
  }
  return undefined;
}
